import { supabase } from './supabase';

export type LeadPayload = {
  home_address: string;
  zip: string;
  city: string;
  state: string;
  recipient_relationship: string;
  project_type: string;
  project_label: string;
  mobility_need: string;
  timeline: string;
  budget: string;
  preferred_date: string | null;
  contact_name: string;
  phone: string;
  phone_display: string;
  email: string;
  referral_source: string | null;
  page_path: string;
};

export type LeadResult = { ok: boolean; reference: string; shareToken?: string };

// Short human-friendly reference so the family has something to hold onto.
function makeReference() {
  const n = Math.floor(1000 + Math.random() * 9000);
  return `ROSA-${n}`;
}

export function getReferralSource(): string | null {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get('ref') || p.get('partner') || p.get('utm_source') || null;
  } catch {
    return null;
  }
}

export async function submitLead(payload: LeadPayload): Promise<LeadResult> {
  const reference = makeReference();
  if (!supabase) {
    // Graceful path if the backend isn't provisioned — never lose the user's effort.
    return { ok: true, reference };
  }
  try {
    const { data, error } = await supabase.functions.invoke('submit-lead', {
      body: { ...payload, reference },
    });
    if (error) throw error;
    return { ok: true, reference: data?.reference ?? reference, shareToken: data?.shareToken };
  } catch {
    // Fallback: attempt a direct insert (RLS allows anonymous lead capture).
    try {
      const { error } = await supabase.from('leads').insert({
        reference,
        home_address: payload.home_address,
        zip: payload.zip,
        city: payload.city,
        state: payload.state,
        recipient_relationship: payload.recipient_relationship,
        project_type: payload.project_type,
        mobility_need: payload.mobility_need,
        timeline: payload.timeline,
        budget: payload.budget,
        preferred_date: payload.preferred_date,
        contact_name: payload.contact_name,
        phone: payload.phone,
        email: payload.email,
        referral_source: payload.referral_source,
        page_path: payload.page_path,
      });
      if (error) throw error;
      return { ok: true, reference };
    } catch {
      return { ok: false, reference };
    }
  }
}
